import type { StateMachine } from './machine';

/**
 * Statut du DOSSIER (cahier des charges §7), strictement distinct du statut d'intervention
 * (Proposé, Accepté, Planifié, Pris en charge, Sur place, Terminé, Impossible → intervention.ts)
 * et du statut de paiement (payment.ts).
 *
 * Les passages « en intervention » / « terminé » sont pilotés par le module interventions
 * (acteur `system`) : un utilisateur ne peut pas les forcer.
 */
export const DOSSIER_STATUSES = [
  'draft',
  'submitted',
  'to_correct',
  'validated',
  'to_schedule',
  'in_progress',
  'completed',
  'cancelled',
] as const;
export type DossierStatus = (typeof DOSSIER_STATUSES)[number];

const CANCELLABLE: DossierStatus[] = ['draft', 'submitted', 'to_correct', 'validated', 'to_schedule', 'in_progress'];

export const dossierMachine: StateMachine<DossierStatus> = {
  name: 'dossier',
  states: DOSSIER_STATUSES,
  initial: 'draft',
  terminal: ['completed', 'cancelled'],
  labels: {
    draft: 'Brouillon',
    submitted: 'À contrôler',
    to_correct: 'À corriger',
    validated: 'Validé',
    to_schedule: 'À planifier',
    in_progress: 'En intervention',
    completed: 'Terminé',
    cancelled: 'Annulé',
  },
  transitions: [
    { from: ['draft', 'to_correct'], to: 'submitted', permission: 'dossier.submit' },
    // Contrôle manuel : renvoi avec motif (contrôleur identifié) ou validation.
    { from: ['submitted', 'validated'], to: 'to_correct', permission: 'dossier.control', reasonCategory: 'case_return' },
    { from: ['submitted'], to: 'validated', permission: 'dossier.control' },
    // « Un dossier ne passe en planification qu'après contrôle manuel ».
    { from: ['validated'], to: 'to_schedule', permission: 'dossier.schedule' },
    // Chantier pour un partenaire (sous-traitance de pose, sans vente) : envoyé directement en planification.
    // Réservé aux dossiers « partenaire » (contrôle côté serveur et dans l'application).
    { from: ['draft'], to: 'to_schedule', permission: 'dossier.schedule' },
    { from: ['to_schedule'], to: 'in_progress', permission: null, actors: ['system'] },
    { from: ['in_progress'], to: 'to_schedule', permission: null, actors: ['system'] },
    { from: ['in_progress'], to: 'completed', permission: null, actors: ['system'] },
    { from: CANCELLABLE, to: 'cancelled', permission: 'dossier.cancel', reasonCategory: 'case_cancel' },
  ],
};

/** Statuses in which the commercial may still edit the dossier content. */
export const DOSSIER_EDITABLE: DossierStatus[] = ['draft', 'to_correct'];
/** Statuses in which an admin/supervisor (dossier.control) may still edit it. */
export const DOSSIER_EDITABLE_BY_CONTROL: DossierStatus[] = ['draft', 'submitted', 'to_correct', 'validated', 'to_schedule'];
